import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

// Apply autoTable plugin to jsPDF
applyPlugin(jsPDF);

// Simple currency formatter for PDF (avoids encoding issues)
const formatCurrencyForPDF = (amount) => {
  if (!amount || isNaN(amount)) return 'Rs. 0';
  return `Rs. ${Number(amount).toLocaleString('en-IN')}`;
};

// Consortium Gifts logo as base64 (to be loaded)
let logoDataUrl = null;

// Function to load logo image
const loadLogo = async () => {
  if (logoDataUrl) return logoDataUrl;
  
  try {
    const response = await fetch('/consortium-logo.jpg');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load logo:', error);
    return null;
  }
};

export const generateProposalPDF = async (products, clientName = 'Valued Client') => {
  try {
    console.log('Starting PDF generation...');
    console.log('Products:', products);
    console.log('Client Name:', clientName);
    
    // Load logo
    logoDataUrl = await loadLogo();
    
    const doc = new jsPDF();
    
    // Verify autoTable is available
    if (typeof doc.autoTable !== 'function') {
      throw new Error('jsPDF autoTable plugin not loaded');
    }
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

  // Header with white background
  doc.setFillColor(255, 255, 255); // White background
  doc.rect(0, 0, pageWidth, 50, 'F');
  
  // Add logo image
  if (logoDataUrl) {
    try {
      // Center logo in header - increased height for better proportions
      const logoWidth = 100;
      const logoHeight = 45;
      const logoX = (pageWidth - logoWidth) / 2;
      const logoY = 2.5;
      
      doc.addImage(logoDataUrl, 'JPEG', logoX, logoY, logoWidth, logoHeight);
    } catch (error) {
      console.error('Failed to add logo to PDF:', error);
      // Fallback to text
      doc.setTextColor(2, 102, 59); // Green text
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('CONSORTIUM GIFTS', pageWidth / 2, 28, { align: 'center' });
    }
  } else {
    // Fallback to text logo if image failed to load
    doc.setTextColor(2, 102, 59); // Green text
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('CONSORTIUM GIFTS', pageWidth / 2, 28, { align: 'center' });
  }

  // Client Info
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('PRODUCT PROPOSAL', 20, 65);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Prepared for: ${clientName}`, 20, 75);
  doc.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, 20, 82);

  // Products Table
  let yPosition = 93;
  
  products.forEach((product, index) => {
    if (yPosition > pageHeight - 60) {
      doc.addPage();
      yPosition = 20;
    }

    // Product Header
    doc.setFillColor(240, 253, 244); // Light green
    doc.rect(15, yPosition - 5, pageWidth - 30, 8, 'F');
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(2, 102, 59);
    doc.text(`${index + 1}. ${product.name}`, 20, yPosition);
    
    yPosition += 10;

    // Product Details
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    const details = [
      [`Category: ${product.category || 'N/A'}`, `Price: ${formatCurrencyForPDF(product.recommended_selling_price || 0)} per unit`],
      [`MOQ: ${product.moq || 'N/A'} units`, `Stock: ${product.stock_status || 'N/A'}`],
      [`Delivery: ${product.delivery_timeline || 'Contact for details'}`, ``],
    ];

    details.forEach(([left, right]) => {
      doc.text(left, 20, yPosition);
      if (right) doc.text(right, 120, yPosition);
      yPosition += 5;
    });

    // Description
    doc.setFontSize(8);
    doc.setTextColor(60, 60, 60);
    const description = product.description || 'No description available';
    const splitDescription = doc.splitTextToSize(description, pageWidth - 50);
    doc.text(splitDescription.slice(0, 2), 20, yPosition); // Max 2 lines
    yPosition += 10;

    // Branding Options
    const brandingOptions = [];
    if (product.branding_options) {
      if (product.branding_options.logo_print) brandingOptions.push('Logo Print');
      if (product.branding_options.embroidery) brandingOptions.push('Embroidery');
      if (product.branding_options.engraving) brandingOptions.push('Engraving');
      if (product.branding_options.full_color_print) brandingOptions.push('Full Color');
      if (product.branding_options.debossing) brandingOptions.push('Debossing');
      if (product.branding_options.packaging_customization) brandingOptions.push('Custom Packaging');
    }

    if (brandingOptions.length > 0) {
      doc.setFontSize(8);
      doc.setTextColor(2, 102, 59);
      doc.text(`Branding: ${brandingOptions.join(', ')}`, 20, yPosition);
      yPosition += 7;
    }

    yPosition += 5; // Space between products
  });

  // Summary Table
  if (yPosition > pageHeight - 80) {
    doc.addPage();
    yPosition = 20;
  }

  yPosition += 10;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('PRICING SUMMARY', 20, yPosition);
  yPosition += 8;

  const tableData = products.map((product, idx) => [
    `${idx + 1}`,
    product.name || 'Unnamed Product',
    formatCurrencyForPDF(product.recommended_selling_price || 0),
    (product.moq || 0).toString(),
    formatCurrencyForPDF((product.recommended_selling_price || 0) * (product.moq || 0))
  ]);

  const total = products.reduce((sum, p) => sum + ((p.recommended_selling_price || 0) * (p.moq || 0)), 0);

  doc.autoTable({
    startY: yPosition,
    head: [['#', 'Product', 'Unit Price', 'MOQ', 'Subtotal']],
    body: tableData,
    foot: [[{ content: `Total (at MOQ): ${formatCurrencyForPDF(total)}`, colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } }]],
    theme: 'grid',
    headStyles: { fillColor: [2, 102, 59], textColor: [255, 255, 255] },
    footStyles: { fillColor: [240, 253, 244], textColor: [2, 102, 59], fontStyle: 'bold' },
    margin: { left: 20, right: 20 },
  });

  // Footer
  const finalY = doc.lastAutoTable.finalY || yPosition + 50;
  if (finalY < pageHeight - 40) {
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('For queries, please contact: info@consortiumgifts.com | +91 96500 23803', pageWidth / 2, pageHeight - 20, { align: 'center' });
    doc.text('© 2026 Consortium Gifts Pvt Ltd. All rights reserved.', pageWidth / 2, pageHeight - 15, { align: 'center' });
  }

  // Generate filename
  const filename = `Consortium_Proposal_${new Date().toISOString().split('T')[0]}.pdf`;
  
  console.log('PDF generation complete. Downloading:', filename);
  
  // Save PDF
  doc.save(filename);
  
  console.log('PDF saved successfully');
  } catch (error) {
    console.error('Error in generateProposalPDF:', error);
    throw new Error(`PDF Generation failed: ${error.message}`);
  }
};
